/**
 * IndexedDB Database Manager for Astronomical Objects
 * 
 * This module provides fast, offline-capable storage and search for
 * astronomical objects using IndexedDB in the browser.
 */

import { chooseDisplayName } from './displayNames.js';

export class DatabaseManager {
    constructor(dbName = 'AstroObjectsDB', version = 1) {
        this.dbName = dbName;
        this.version = version;
        this.db = null;
        this.isInitialized = false;
    }

    /**
     * Initialize the IndexedDB database
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        if (this.isInitialized) {
            return true;
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = () => {
                console.error('Failed to open IndexedDB:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                this.isInitialized = true;
                console.log('IndexedDB initialized successfully');
                resolve(true);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                this.createObjectStores(db);
            };
        });
    }

    /**
     * Create object stores in the database
     * @param {IDBDatabase} db - Database instance
     */
    createObjectStores(db) {
        // Main objects store
        if (!db.objectStoreNames.contains('objects')) {
            const objectsStore = db.createObjectStore('objects', { keyPath: 'id', autoIncrement: true });
            objectsStore.createIndex('name', 'name', { unique: true });
            objectsStore.createIndex('catalog', 'catalog', { unique: false });
            objectsStore.createIndex('catalog_number', 'catalog_number', { unique: false });
            objectsStore.createIndex('magnitude', 'magnitude', { unique: false });
            objectsStore.createIndex('object_type', 'object_type', { unique: false });
            objectsStore.createIndex('ra_deg', 'ra_deg', { unique: false });
            objectsStore.createIndex('dec_deg', 'dec_deg', { unique: false });
        }

        // Aliases store
        if (!db.objectStoreNames.contains('aliases')) {
            const aliasesStore = db.createObjectStore('aliases', { keyPath: 'id', autoIncrement: true });
            aliasesStore.createIndex('object_id', 'object_id', { unique: false });
            aliasesStore.createIndex('alias_name', 'alias_name', { unique: false });
            aliasesStore.createIndex('priority', 'priority', { unique: false });
        }

        // Search index store for fast text search
        if (!db.objectStoreNames.contains('searchIndex')) {
            const searchStore = db.createObjectStore('searchIndex', { keyPath: 'id', autoIncrement: true });
            searchStore.createIndex('searchText', 'searchText', { unique: false });
            searchStore.createIndex('object_id', 'object_id', { unique: false });
        }
    }

    /**
     * Add an astronomical object to the database
     * @param {Object} object - Astronomical object data
     * @returns {Promise<number>} Object ID
     */
    async addObject(object) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects', 'aliases', 'searchIndex'], 'readwrite');
            
            const objectsStore = transaction.objectStore('objects');
            const aliasesStore = transaction.objectStore('aliases');
            const searchStore = transaction.objectStore('searchIndex');

            // Add main object
            const addRequest = objectsStore.add(object);
            
            addRequest.onsuccess = () => {
                const objectId = addRequest.result;
                
                // Add aliases if they exist
                if (object.aliases && object.aliases.length > 0) {
                    object.aliases.forEach((alias, index) => {
                        aliasesStore.add({
                            object_id: objectId,
                            alias_name: alias,
                            priority: 100 - index // Higher priority for earlier aliases
                        });
                    });
                }

                // Add to search index
                const searchText = this.createSearchText(object);
                searchStore.add({
                    object_id: objectId,
                    searchText: searchText
                });

                resolve(objectId);
            };

            addRequest.onerror = () => {
                reject(addRequest.error);
            };
        });
    }

    /**
     * Add multiple objects in batch
     * @param {Array} objects - Array of astronomical objects
     * @returns {Promise<Array>} Array of object IDs
     */
    async addObjectsBatch(objects) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        const objectIds = [];
        
        for (const object of objects) {
            try {
                const id = await this.addObject(object);
                objectIds.push(id);
            } catch (error) {
                console.error('Error adding object:', object.name, error);
            }
        }

        return objectIds;
    }

    /**
     * Search for objects by query
     * @param {string} query - Search query
     * @param {number} maxResults - Maximum number of results
     * @returns {Promise<Array>} Array of matching objects
     */
    async searchObjects(query, maxResults = 15) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        if (!query || query.length < 1) {
            return [];
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects', 'aliases', 'searchIndex'], 'readonly');
            const objectsStore = transaction.objectStore('objects');
            const aliasesStore = transaction.objectStore('aliases');
            const searchStore = transaction.objectStore('searchIndex');

            const results = [];
            const seenIds = new Set();

            // Search in main objects
            const objectsRequest = objectsStore.getAll();
            objectsRequest.onsuccess = () => {
                const allObjects = objectsRequest.result;
                
                // Score and filter objects
                const scoredObjects = allObjects.map(obj => {
                    const score = this.calculateRelevanceScore(obj, query);
                    return { object: obj, score };
                }).filter(item => item.score > 0)
                  .sort((a, b) => b.score - a.score)
                  .slice(0, maxResults);

                scoredObjects.forEach(item => {
                    if (!seenIds.has(item.object.id)) {
                        results.push(item.object);
                        seenIds.add(item.object.id);
                    }
                });

                // Search in aliases
                const aliasesRequest = aliasesStore.getAll();
                aliasesRequest.onsuccess = () => {
                    const allAliases = aliasesRequest.result;
                    const matchingAliases = allAliases.filter(alias => 
                        alias.alias_name.toLowerCase().includes(query.toLowerCase())
                    );

                    // Get objects for matching aliases
                    matchingAliases.forEach(alias => {
                        if (!seenIds.has(alias.object_id) && results.length < maxResults) {
                            const getRequest = objectsStore.get(alias.object_id);
                            getRequest.onsuccess = () => {
                                if (getRequest.result) {
                                    results.push(getRequest.result);
                                    seenIds.add(alias.object_id);
                                }
                            };
                        }
                    });

                    resolve(results.slice(0, maxResults));
                };

                aliasesRequest.onerror = () => {
                    reject(aliasesRequest.error);
                };
            };

            objectsRequest.onerror = () => {
                reject(objectsRequest.error);
            };
        });
    }

    /**
     * Calculate relevance score for search results
     * @param {Object} object - Astronomical object
     * @param {string} query - Search query
     * @returns {number} Relevance score
     */
    calculateRelevanceScore(object, query) {
        const queryLower = query.toLowerCase();
        const nameLower = object.name.toLowerCase();
        let score = 0;

        // Exact match gets highest score
        if (nameLower === queryLower) {
            score += 1000;
        }
        // Starts with query
        else if (nameLower.startsWith(queryLower)) {
            score += 500;
        }
        // Contains query
        else if (nameLower.includes(queryLower)) {
            score += 200;
        }
        // Check catalog number
        else if (object.catalog_number && object.catalog_number.includes(query)) {
            score += 300;
        }

        // Bonus for popular catalogs
        if (object.catalog) {
            const catalog = object.catalog.toLowerCase();
            if (['messier', 'ngc', 'ic'].includes(catalog)) {
                score += 100;
            } else if (['sharpless', 'caldwell'].includes(catalog)) {
                score += 50;
            }
        }

        // Bonus for brighter objects
        if (object.magnitude !== null && object.magnitude !== undefined) {
            score += 20;
            if (object.magnitude < 10) score += 10;
            if (object.magnitude < 5) score += 20;
        }

        return score;
    }

    /**
     * Create searchable text for an object
     * @param {Object} object - Astronomical object
     * @returns {string} Searchable text
     */
    createSearchText(object) {
        const parts = [
            object.name,
            object.catalog,
            object.catalog_number,
            object.object_type,
            object.description
        ].filter(part => part !== null && part !== undefined);

        return parts.join(' ').toLowerCase();
    }

    /**
     * Get object by exact name
     * @param {string} name - Object name
     * @returns {Promise<Object|null>} Object or null
     */
    async getObjectByName(name) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects'], 'readonly');
            const store = transaction.objectStore('objects');
            const index = store.index('name');
            const request = index.get(name);

            request.onsuccess = () => {
                resolve(request.result || null);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Get all objects in a catalog
     * @param {string} catalog - Catalog name
     * @returns {Promise<Array>} Array of objects
     */
    async getObjectsByCatalog(catalog) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects'], 'readonly');
            const store = transaction.objectStore('objects');
            const index = store.index('catalog');
            const request = index.getAll(catalog);

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Get database statistics
     * @returns {Promise<Object>} Database statistics
     */
    async getStats() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects', 'aliases'], 'readonly');
            const objectsStore = transaction.objectStore('objects');
            const aliasesStore = transaction.objectStore('aliases');

            const stats = {
                totalObjects: 0,
                totalAliases: 0,
                catalogStats: {}
            };

            // Count total objects
            const objectsRequest = objectsStore.count();
            objectsRequest.onsuccess = () => {
                stats.totalObjects = objectsRequest.result;

                // Count total aliases
                const aliasesRequest = aliasesStore.count();
                aliasesRequest.onsuccess = () => {
                    stats.totalAliases = aliasesRequest.result;

                    // Get catalog statistics
                    const getAllRequest = objectsStore.getAll();
                    getAllRequest.onsuccess = () => {
                        const objects = getAllRequest.result;
                        const catalogCounts = {};

                        objects.forEach(obj => {
                            if (obj.catalog) {
                                catalogCounts[obj.catalog] = (catalogCounts[obj.catalog] || 0) + 1;
                            }
                        });

                        stats.catalogStats = catalogCounts;
                        resolve(stats);
                    };

                    getAllRequest.onerror = () => {
                        reject(getAllRequest.error);
                    };
                };

                aliasesRequest.onerror = () => {
                    reject(aliasesRequest.error);
                };
            };

            objectsRequest.onerror = () => {
                reject(objectsRequest.error);
            };
        });
    }

    /**
     * Clear all data from the database
     * @returns {Promise<boolean>} Success status
     */
    async clearDatabase() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects', 'aliases', 'searchIndex'], 'readwrite');
            
            const clearRequests = [
                transaction.objectStore('objects').clear(),
                transaction.objectStore('aliases').clear(),
                transaction.objectStore('searchIndex').clear()
            ];

            let completed = 0;
            let hasError = false;

            clearRequests.forEach(request => {
                request.onsuccess = () => {
                    completed++;
                    if (completed === clearRequests.length && !hasError) {
                        resolve(true);
                    }
                };

                request.onerror = () => {
                    hasError = true;
                    reject(request.error);
                };
            });
        });
    }

    /**
     * Import data from JSON
     * @param {Array} objects - Array of objects to import
     * @param {Array} aliases - Array of aliases to import (optional)
     * @returns {Promise<number>} Number of objects imported
     */
    async importData(objects, aliases = []) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        console.log(`Importing ${objects.length} objects and ${aliases.length} aliases...`);

        let imported = 0;
        const objectNameToId = new Map();

        // First, import all objects
        for (const object of objects) {
            try {
                const objectId = await this.addObject(object);
                objectNameToId.set(object.name, objectId);
                imported++;
            } catch (error) {
                console.error('Error importing object:', object.name, error);
            }
        }

        // Then, import aliases
        if (aliases.length > 0) {
            console.log(`Importing ${aliases.length} aliases...`);
            let aliasesImported = 0;

            const transaction = this.db.transaction(['aliases'], 'readwrite');
            const aliasesStore = transaction.objectStore('aliases');

            for (const aliasData of aliases) {
                try {
                    const objectId = objectNameToId.get(aliasData.object_name);
                    if (objectId) {
                        await new Promise((resolve, reject) => {
                            const request = aliasesStore.add({
                                object_id: objectId,
                                alias_name: aliasData.alias,
                                priority: 50 // Default priority for imported aliases
                            });
                            request.onsuccess = () => resolve(request.result);
                            request.onerror = () => reject(request.error);
                        });
                        aliasesImported++;
                    } else {
                        console.warn(`Object not found for alias: ${aliasData.alias} -> ${aliasData.object_name}`);
                    }
                } catch (error) {
                    console.error('Error importing alias:', aliasData.alias, error);
                }
            }

            console.log(`Successfully imported ${aliasesImported} aliases`);
        }

        console.log(`Successfully imported ${imported} objects`);
        return imported;
    }

    /**
     * Export all data to JSON
     * @returns {Promise<Array>} Array of all objects
     */
    async exportData() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects'], 'readonly');
            const store = transaction.objectStore('objects');
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Export all alias rows ({object_id, alias_name, priority})
     * @returns {Promise<Array>} Array of all alias records
     */
    async exportAliases() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['aliases'], 'readonly');
            const request = transaction.objectStore('aliases').getAll();

            request.onsuccess = () => {
                resolve(request.result || []);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Get the count of objects in the database
     * @returns {Promise<number>} Number of objects
     */
    async getObjectCount() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['objects'], 'readonly');
            const store = transaction.objectStore('objects');
            const countRequest = store.count();

            countRequest.onsuccess = () => {
                resolve(countRequest.result);
            };

            countRequest.onerror = () => {
                reject(countRequest.error);
            };
        });
    }

    /**
     * Get aliases for an object by object ID
     * @param {number} objectId - Object ID
     * @returns {Promise<Array>} Array of aliases sorted by priority
     */
    async getObjectAliases(objectId) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['aliases'], 'readonly');
            const store = transaction.objectStore('aliases');
            const index = store.index('object_id');
            const request = index.getAll(objectId);

            request.onsuccess = () => {
                const aliases = request.result || [];
                // Sort by priority (highest first)
                aliases.sort((a, b) => b.priority - a.priority);
                resolve(aliases);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    }

    /**
     * Get the preferred display name for an object
     * @param {Object} object - Object from database
     * @returns {Promise<string>} Preferred display name
     */
    async getPreferredDisplayName(object) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            const aliases = object.id !== undefined ? await this.getObjectAliases(object.id) : [];
            return chooseDisplayName(object, aliases.map((alias) => alias.alias_name));
        } catch (error) {
            console.error('Error getting preferred display name:', error);
            return chooseDisplayName(object);
        }
    }
}

export default DatabaseManager;

